<template>
  <main class="user-profile">
    <div
      class="user-profile__container"
      v-if="isLoad">
      <div class="user-profile__header">
        <h1 class="user-profile__title">Мой профиль</h1>
        <button
          type="button"
          class="user-profile__icon-edit"
          v-on:click="hasOpenForm = true" />
      </div>
      <div class="user-profile__row">
        <form-profile-photo
          @form-submit="uploadProfilePhoto"
          @delete-photo="deleteProfilePhoto"
          :photoUrl="profile.photo_url"
        />
        <div class="user-profile__body">
          <div class="user-profile__field">
            <span class="user-profile__field__caption">ФИО:</span>
            <span>{{fullName}}</span>
          </div>
          <div class="user-profile__field">
            <span class="user-profile__field__caption">E-mail: </span>
            <span>{{profile.email}}</span>
          </div>
          <modal-dialog
            v-model="showDialog"
            title="Выслать ссылку для создания нового пароля на email?"
            with-cross
            max-width="500px"
          >
            <template v-slot:trigger="{ listenerObject }">
              <button
                class="user-profile__link"
                type="button"
                v-on="listenerObject">Сменить пароль</button>
            </template>
            <div class="user-profile__buttons">
              <button
                class="user-profile__button-cancel"
                v-on:click="showDialog = false"
                type="button">
                Отмена
              </button>
              <button
                class="user-profile__button-save"
                v-on:click="onSendPassword"
                type="button">
                Выслать
              </button>
            </div>
          </modal-dialog>
        </div>
      </div>
      <div class="user-profile__safety">
        <div class="user-profile__ip-address">
          <p>
            <b>Ваш IP-адрес:</b> {{ ipAddress }}
          </p>
          <button
            type="button"
            class="user-profile__button-copy"
            title="Скопировать"
            v-on:click="copyIpAddressToClipboard" />
        </div>
      </div>
    </div>
    <side-slide-transition>
      <side-dialog
        v-if="hasOpenForm"
        label="Профиль"
        v-on:cancel="hasOpenForm = false">
        <form-edit-user-profile
          v-bind:initial-profile="profile"
          v-bind:input-errors="inputErrors"
          v-on:submit="onSaveProfile"
          v-on:cancel="hasOpenForm = false" />
      </side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import { List } from 'monet';
import notifierMixin from '@/mixins/notifierMixin';
import { EditUserProfileRequest, UserProfile } from '@/storage/gateways/UserGateway';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import SideDialog from '@/components/SideDialog.vue';
import FormEditUserProfile from '@/components/FormEditUserProfile.vue';
import FormProfilePhoto from '@/components/FormProfilePhoto.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import clipboardMixin from '@/mixins/clipboardMixin';

export default {
  components: {
    SideSlideTransition,
    SideDialog,
    FormEditUserProfile,
    FormProfilePhoto,
    ModalDialog,
  },
  computed: {
    /**
     * @description Полное имя (ФИО).
     * @type {string}
     */
    fullName: ({ profile }) => [profile.surname, profile.name, profile.patronymic].join(' '),
    /**
     * @description Профиль загружен.
     * @type {boolean}
     */
    isLoad: ({ profile }) => !!profile.email,
  },
  created() {
    this.fetchProfile();
  },
  data() {
    return {
      /**
       * @description Форма открыта.
       * @type {boolean}
       */
      hasOpenForm: false,
      /**
       * @description Нарушения ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description IP-адрес пользователя.
       * @type {string}
       */
      ipAddress: '',
      /**
       * @description Профиль пользователя.
       * @type {UserProfile}
       */
      profile: new UserProfile(),
      /**
       * @description Показывать диалог.
       * @type {boolean}
       */
      showDialog: false,
    };
  },
  methods: {
    /**
     * @description Скопировать IP-адресс в буфер обмена.
     * @returns {void}
     */
    copyIpAddressToClipboard() {
      this.toClipboard(this.ipAddress).then(
        () => { this.notifySuccess('IP-адрес успешно скопирован.'); },
        () => { this.notifyFail('Не удалось скопировать IP-адрес.'); },
      );
    },
    /**
     * @description Загрузить фотографию на бек
     * @param {Object} imageData объект, содержащий файл фото
     */
    uploadProfilePhoto(imageData) {
      if (imageData) {
        const form = new FormData();
        form.append('profilePhoto', imageData.file);
        this.$userGateway.uploadPhoto(form).then((result) => {
          result.forEach(({ fileSrc }) => {
            this.$store.dispatch('USER_SET_PROFILE_PHOTO', fileSrc);
            this.setProfilePhoto(fileSrc);
          });
          result.leftMap(
            ({ causes, title }) => List
              .from(Object.values(causes))
              .flatMap(({ details }) => List.fromArray(details))
              .foldLeft(`${title}.`)((acc, detail) => `${acc} ${detail}`),
          ).forEachLeft((error) => {
            this.notifyFail(error);
          });
        });
      }
    },
    deleteProfilePhoto() {
      this.$userGateway.deletePhoto().then((result) => {
        result.forEach(() => {
          this.clearProfilePhoto();
          this.notifySuccess('Фотография профиля удалена');
        });
        result.leftMap(
          ({ causes, title }) => List
            .from(Object.values(causes))
            .flatMap(({ details }) => List.fromArray(details))
            .foldLeft(`${title}.`)((acc, detail) => `${acc} ${detail}`),
        ).forEachLeft((error) => {
          this.notifyFail(error);
        });
      });
    },
    /**
    * @description очистить урл фотографии профиля пользователя
    */
    clearProfilePhoto() {
      this.profile.photo_url = '';
    },
    /**
     * @description установить новый урл фотографии в профиль пользователя
     * @param {string} url полный путь до фотографии
     */
    setProfilePhoto(url) {
      this.profile.photo_url = url;
    },
    /**
     * @description Получить профиль пользователя.
     * @returns {void}
     */
    fetchProfile() {
      this.$userGateway.viewProfile().then((either) => {
        either.forEach(({ profile, ipAddress }) => {
          this.profile = profile;
          this.ipAddress = ipAddress;
        });
        either.forEachFail((error) => {
          if (error.title) {
            this.notifyFail(error.title);
          }
        });
      });
    },
    /**
     * @description Обработать сохранение профиля.
     * @param {UserProfile} profile Профиль пользователя.
     * @returns {void}
     */
    async onSaveProfile(profile) {
      const request = new EditUserProfileRequest(profile);
      const either = await this.$userGateway.edit(request);
      either.forEach(({ message }) => {
        this.inputErrors = {};
        this.profile = profile;
        this.hasOpenForm = false;
        this.notifySuccess(message);
      });
      either.forEachFail((error) => {
        const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs);
      });
    },
    /**
     * @description Обработать отправления письма для смены пароля.
     * @returns {void}
     */
    onSendPassword() {
      this
        .$authGateway
        .sendPasswordRecoveryEmail(this.profile.email)
        .then((result) => {
          result.forEach(({ message }) => {
            this.notifySuccess(message);
            this.inputErrors = {};
          });
          result.forEachLeft(({ message }) => {
            this.notifyFail(message);
          });
        });
      this.showDialog = false;
    },
  },
  name: 'ClinicPanelUserProfile',
  mixins: [
    notifierMixin,
    clipboardMixin,
  ],
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.user {
  &-profile {
    font-family: "Lato", sans-serif;
    height: 100%;
    width: 100%;
    &__container {
      height: 100%;
      overflow-y: scroll;
      padding: 38px 20px 48px 23px;
      width: 100%;
      flex-direction: column;
      display: flex;
      gap: 10px;
    }
    &__header {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      gap: 22px;
      margin: 0 0 57px 0;
    }
    &__title {
      font-weight: 600;
      font-size: 38px;
      line-height: 46px;
      color: #000000;
    }
    &__icon-edit {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      background: #EAEFF3;
      border-radius: 3px;
      width: 31px;
      min-width: 31px;
      height: 31px;
      min-height: 31px;
      &:before {
        content: '';
        background-image: url("@/assets/img/pen-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 22px;
        height: 22px;
      }
      &:hover:before {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &__row {
      display: grid;
      grid-template-columns: 177px 1fr;
      grid-gap: 75px;
      margin-bottom: 30px;
    }
    & &__ip-address {
      display: flex;
      column-gap: 8px;
      align-items: center;
      p {
        display: block;
        margin: 0;
      }
    }
    &__body {
      background: #F2F5F8;
      padding: 30px 37px;
      width: 100%;
      max-width: 950px;
    }
    &__field {
      display: grid;
      grid-template-columns: 102px 1fr;
      grid-gap: 26px;
      font-weight: 400;
      font-size: 18px;
      line-height: 22px;
      color: #192A3E;
      margin: 0 0 26px 0;
      &__caption {
        color: #90A0B7;
      }
    }
    &__link {
      display: block;
      font-weight: 400;
      font-size: 18px;
      line-height: 22px;
      text-decoration: underline;
      color: var(--action-color);
      cursor: pointer;
      margin: 48px 0 0 0;
      &:hover {
        text-decoration: none;
      }
    }
    &__form {
      margin-top: 50px;
      flex-direction: column;
      display: flex;
      gap: 10px;
      &__row {
        flex-direction: row;
        display: flex;
        gap: 10px;
      }
      &__multiline {
        margin-top: 10px;
      }
      &__buttons {
        margin: 50px 0 0 auto;
        flex-direction: row;
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        max-width: 438px;
        width: 100%;
      }
      &__button {
        &-cancel {
          max-width: 219px;
          @include buttons.btn-transparent(100%, 56px, 20px);
        }
        &-save {
          max-width: 219px;
          @include buttons.btn-blue(100%, 56px, 20px);
        }
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      align-items: center;
      margin: 20px 0 0 0;
    }
    &__button {
      &-cancel {
        @include buttons.btn-transparent(100%, 40px, 16px);
      }
      &-save {
        @include buttons.btn-blue(100%, 40px, 16px);
      }
      &-copy {
        width: 18px;
        height: 18px;
        background-image: url("@/assets/img/copy-grey.svg");
        background-repeat: no-repeat;
        background-position: 50%;
        background-size: contain;
        transition: background-image 0.3s;
        &:hover {
          background-image: url("@/assets/img/copy-blue.svg");
        }
      }
    }
  }
}
@media screen and (max-width: 960px) {
  .user-profile {
    &__row {
      grid-template-columns: 1fr;
      grid-gap: 30px;
    }
  }
}
@media screen and (max-width: 600px) {
  .user-profile {
    &__container {
      padding: 45px 10px 140px;
    }
    &__field {
      grid-template-columns: 1fr;
      grid-gap: 10px;
    }
  }
}
</style>
